// --- Dependencies -------------------------------------------------------------------------------
import { Link } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInstallment } from "@/types";
import { formatDate, formatMoney } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { installment: TyInstallment; onPay?: () => void };

/**
 * One amount due on one date.
 *
 * **Overdue is derived, not stored** — a row is late when it is still `pending` and its date has
 * passed, which is a comparison rather than a column some nightly job has to remember to write. So
 * the card works it out at the moment it draws, and is never a day behind.
 *
 * The remainder is the figure, not the total. A part-paid installment is the common case once a
 * plan has been running, and "60,000 of 100,000" answers nothing a person collecting money needs;
 * "40,000 left" does.
 */
export default function InstallmentCard({ installment, onPay }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const customer = typeof installment.customer === "object" && installment.customer ? installment.customer : null;
    const plan = typeof installment.plan === "object" && installment.plan ? installment.plan : null;
    const unit = plan && typeof plan.unit === "object" && plan.unit ? plan.unit.number : null;

    const due = installment.amount ?? 0;
    const left = Math.max(due - (installment.paidAmount ?? 0), 0);
    const isPaid = installment.status === "paid";
    const isLate = !isPaid && !!installment.dueAt && new Date(installment.dueAt) < new Date();
    const state = isLate ? "overdue" : isPaid ? "paid" : "pending";

    return (
        <article className="installment-card" data-state={state}>
            <div className="installment-card__top">
                <span className="installment-card__mark" aria-hidden="true">
                    <Icon name="calendar" size={16} />
                </span>

                <div className="installment-card__identity">
                    <span className="installment-card__when">{installment.dueAt ? formatDate(installment.dueAt, lang) : t.crm.notSet}</span>
                    <span className="installment-card__which">
                        {t.sales.installmentOf.replace("{number}", String(installment.number)).replace("{count}", String(installment.number))}
                        {unit ? ` · ${unit}` : ""}
                    </span>
                </div>

                <span className="installment-card__state">{isLate ? t.sales.overdue : t.sales.statuses[installment.status]}</span>
            </div>

            {customer && (
                <Link className="installment-card__person" to={`/crm/customers/${customer._id}`}>
                    <Avatar name={customer.name} picture={customer.picture ?? null} size={26} rounded />
                    <span className="installment-card__name">{customer.name}</span>
                    <Icon name="eye" size={14} />
                </Link>
            )}

            <dl className="installment-card__facts">
                <div className="installment-card__fact">
                    <dt>{t.sales.amount}</dt>
                    <dd>
                        <bdi dir="ltr">{formatMoney(due)}</bdi>
                    </dd>
                </div>
                <div className="installment-card__fact">
                    <dt>{isPaid ? t.sales.paid : t.sales.remaining}</dt>
                    <dd>
                        <bdi dir="ltr">{formatMoney(isPaid ? (installment.paidAmount ?? 0) : left)}</bdi>
                    </dd>
                </div>
            </dl>

            {onPay && !isPaid && (
                <button type="button" className="installment-card__pay" onClick={onPay}>
                    {t.sales.addPayment}
                </button>
            )}
        </article>
    );
}
